import React, { createContext, useContext, useState, useEffect } from 'react';
import type { ReactNode } from 'react';
import {
  getOAuthRedirectUri,
  getSharedCookieDomain,
  setSharedCookie,
  getSharedCookie,
  deleteSharedCookie,
} from '../utils/authRedirect';

interface User {
  id: string;
  username: string;
  isModerator: boolean;
  moderatorSubreddit?: string | null;
  accessToken?: string;
}

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  login: () => void;
  logout: () => void;
  setUser: (user: User | null) => void;
  checkModeratorStatus: (subreddit: string) => Promise<boolean>;
  clearModeratorStatus: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUserState] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const setUser = (u: User | null) => {
    setUserState(u);
    if (u) {
      const json = JSON.stringify(u);
      localStorage.setItem('reddit_user', json);
      if (getSharedCookieDomain()) {
        setSharedCookie('reddit_user', json, 31536000);
      }
    } else {
      localStorage.removeItem('reddit_user');
      deleteSharedCookie('reddit_user');
    }
  };

  useEffect(() => {
    let savedUser = localStorage.getItem('reddit_user');
    if (!savedUser && getSharedCookieDomain()) {
      savedUser = getSharedCookie('reddit_user');
      if (savedUser) {
        try {
          localStorage.setItem('reddit_user', savedUser);
        } catch {
          /* ignore quota */
        }
      }
    }
    if (savedUser) {
      try {
        const userData = JSON.parse(savedUser);
        setUserState(userData);
      } catch (error) {
        console.error('Error parsing saved user:', error);
        localStorage.removeItem('reddit_user');
        deleteSharedCookie('reddit_user');
      }
    }
    setIsLoading(false);
  }, []);

  const login = () => {
    const clientId = import.meta.env.VITE_REDDIT_CLIENT_ID || '8F8tjayjz4_2-Q';
    const redirectUri = getOAuthRedirectUri();
    const state = Math.random().toString(36).substring(7);

    sessionStorage.setItem('reddit_oauth_state', state);
    if (getSharedCookieDomain()) {
      setSharedCookie('reddit_oauth_state', state, 600);
      setSharedCookie('post_login_redirect', window.location.href, 600);
    }

    const authUrl = `https://www.reddit.com/api/v1/authorize?client_id=${clientId}&response_type=code&state=${state}&redirect_uri=${encodeURIComponent(redirectUri)}&duration=permanent&scope=identity`;

    window.location.href = authUrl;
  };

  const logout = () => {
    setUser(null);
  };

  const clearModeratorStatus = () => {
    if (user) {
      const updatedUser = {
        ...user,
        isModerator: false,
        moderatorSubreddit: null
      };
      setUser(updatedUser);
    }
  };

  const checkModeratorStatus = async (subreddit: string): Promise<boolean> => {
    if (!user) return false;
    
    try {
      // Call backend to check moderator status
      const { API_BASE_URL } = await import('../config');
      const response = await fetch(`${API_BASE_URL}/check-moderator/${subreddit}`, {
        headers: {
          'Authorization': `Bearer ${user.username}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('Moderator check result:', data);
        
        // Update the user's moderator status for this subreddit
        const updatedUser = {
          ...user,
          isModerator: data.isModerator,
          moderatorSubreddit: subreddit
        };
        
        setUser(updatedUser);

        return data.isModerator || false;
      }
      
      console.log('Moderator check failed, assuming not moderator');
      return false;
    } catch (error) {
      console.error('Error checking moderator status:', error);
      return false;
    }
  };

  const value: AuthContextType = {
    user,
    isLoading,
    login,
    logout,
    setUser,
    checkModeratorStatus,
    clearModeratorStatus,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
